import { Control, Controller, FieldValues, Path, UseFormSetValue, useWatch } from "react-hook-form";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { GooglePlaceAutocomplete } from "@/components/ui/customs/autocomplete";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

interface RatingAddressFieldsProps<T extends FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  prefix: string;
  title: string;
}

export const RatingAddressFields = <T extends FieldValues>({
  control,
  setValue,
  prefix,
  title,
}: RatingAddressFieldsProps<T>) => {
  const { t } = useTranslation("boRatings");
  const translateMessage = useTranslateMessage();
  const countryCode = useWatch({ control, name: `${prefix}.countryCode` as Path<T> });
  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const showPostalCode = countryRequiresPostalCode(countryCode);

  const handleCountryChange = useCallback(() => {
    setValue(`${prefix}.streetLines` as Path<T>, [""] as any);
    setValue(`${prefix}.streetLine2` as Path<T>, "" as any);
    setValue(`${prefix}.city` as Path<T>, "" as any);
    setValue(`${prefix}.postalCode` as Path<T>, "" as any);
    setValue(`${prefix}.stateOrProvinceCode` as Path<T>, "" as any);
  }, [setValue, prefix]);

  const handlePlaceSelect = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      const parsed = parseGooglePlaceAddress(place, countryCode);
      if (!parsed) return;

      setValue(`${prefix}.streetLines.0` as Path<T>, parsed.street as any);
      setValue(`${prefix}.city` as Path<T>, parsed.city as any);
      setValue(`${prefix}.postalCode` as Path<T>, parsed.postalCode as any);
      if (showStateOrProvince) {
        setValue(`${prefix}.stateOrProvinceCode` as Path<T>, parsed.stateOrProvinceCode as any);
      }
    },
    [setValue, prefix, showStateOrProvince, countryCode],
  );

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        <ControlledCountryComboboxCountryCode
          label={t("addressFields.countryLabel")}
          control={control}
          name={`${prefix}.countryCode` as Path<T>}
          wrapperClassName="col-span-2"
          onChange={handleCountryChange}
        />
        <Controller
          name={`${prefix}.streetLines.0` as Path<T>}
          control={control}
          disabled={!countryCode}
          render={({ field, fieldState: { error } }) => (
            <GooglePlaceAutocomplete
              label={t("addressFields.streetLine1Label")}
              value={field.value || ""}
              onInputChange={(val) => field.onChange(val)}
              onPlaceSelect={handlePlaceSelect}
              error={translateMessage(error?.message)}
              wrapperClassName="col-span-2"
              disabled={!countryCode}
              countryRestrictions={countryCode ? [countryCode] : undefined}
            />
          )}
        />
        <ControlledUnifiedInput
          control={control}
          name={`${prefix}.streetLine2` as Path<T>}
          label={t("addressFields.streetLine2Label")}
          wrapperClassName="col-span-2"
          disabled={!countryCode}
        />
        <ControlledUnifiedInput
          control={control}
          name={`${prefix}.city` as Path<T>}
          label={t("addressFields.cityLabel")}
          wrapperClassName={`${showPostalCode ? "col-span-1" : "col-span-2"}`}
          disabled={!countryCode}
        />
        {showPostalCode && (
          <ControlledUnifiedInput
            control={control}
            name={`${prefix}.postalCode` as Path<T>}
            label={t("addressFields.postalCodeLabel")}
            disabled={!countryCode}
          />
        )}
        {showStateOrProvince && (
          <ControlledUnifiedInput
            className="w-full"
            control={control}
            name={`${prefix}.stateOrProvinceCode` as Path<T>}
            label={t("addressFields.stateOrProvinceLabel")}
            wrapperClassName="col-span-2"
            disabled={!countryCode}
          />
        )}
      </div>
    </div>
  );
};
